import { trackClientEvent } from 'analytics/analytics'
import Clickable from 'components/Common/Clickable'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { ChertoExperiments } from 'constants/experiments'
import useExperiment from 'hooks/Experiments/useExperiment'
import LineAngleRightBIcon from 'LuxKit/Icons/line/LineAngleRightBIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import LoyaltyProgramNameLogo from 'loyalty/components/LoyaltyProgramNameLogo'
import { LOYALTY_LANDING_PAGE } from 'loyalty/constants/loyaltyRoutes'
import LuxPlusLogo from 'luxPlus/components/Logos/LuxPlusLogo'
import { LUXURY_PLUS_PATHS } from 'luxPlus/constants/base'
import { rem } from 'polished'
import { useCallback } from 'react'
import { defineMessages, useIntl } from 'react-intl'
import styled from 'styled-components'

const messages = defineMessages({
  societeHeadline: {
    id: 'rG6uJUQuXzBsIAz2nSAm',
    defaultMessage: 'A more rewarding way to travel',
    description: 'Headline on the Société loyalty card below the homepage trending destinations carousel',
  },
  societeDescription: {
    id: 'nRledxxaKFhZwYaWZt34',
    defaultMessage: 'Our free loyalty program rewards you with points, upgrades and airport transfers as you travel.',
    description: 'Body copy explaining the Société loyalty program on the homepage card',
  },
  luxPlusHeadline: {
    id: '3mDAA8b7ziSFV0fjV4e2',
    defaultMessage: 'More ways to save',
    description: 'Headline on the LUXPLUS+ membership card below the homepage trending destinations carousel',
  },
  luxPlusDescription: {
    id: 'L1ZaP28-fncDki_5Gjp6',
    defaultMessage: 'Save hundreds each year with member-only pricing across hotels, flights, tours and more.',
    description: 'Body copy explaining LUXPLUS+ savings on the homepage card',
  },
})

const Card = styled(Clickable)`
  min-width: 0;
  overflow: hidden;
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${rem(12)};
  background: ${props => props.theme.palette.neutral.surface.normalBackground};

  ${mediaQueryUp.tablet} {
    flex: 1;
  }
`

const Brand = styled(Group)`
  height: ${rem(86)};
  flex-shrink: 0;

  ${mediaQueryUp.desktop} {
    height: auto;
    min-height: ${rem(96)};
    padding: ${rem(16)};
  }
`

const SocieteBrand = styled(Brand)`
  background: linear-gradient(206.69deg,
    ${props => props.theme.palette.product.loyalty.bronze.lightBackground} 16.66%,
    ${props => props.theme.palette.product.loyalty.bronze.normalBackground} 83.34%);
  color: ${props => props.theme.palette.product.loyalty.bronze.normalContrast};

  ${mediaQueryUp.desktop} {
    background: linear-gradient(243.45deg,
      ${props => props.theme.palette.product.loyalty.bronze.lightBackground} 16.66%,
      ${props => props.theme.palette.product.loyalty.bronze.normalBackground} 83.34%);
  }
`

const LuxPlusBrand = styled(Brand)`
  background: ${props => props.theme.palette.product.luxPlus.background};
  color: ${props => props.theme.palette.product.luxPlus.contrast};
`

const SocieteLogoContainer = styled.div`
  width: ${rem(118)};

  ${mediaQueryUp.desktop} {
    width: ${rem(80)};
  }

  svg {
    display: block;
    width: 100%;
    height: auto;
  }
`

const LuxPlusLogoContainer = styled.div`
  width: ${rem(142)};

  ${mediaQueryUp.desktop} {
    width: ${rem(80)};
  }

  svg {
    display: block;
    width: 100%;
    height: auto;
  }
`

const Content = styled(Group)`
  padding: ${rem(16)};
`

const ArrowIcon = styled(LineAngleRightBIcon)`
  margin-inline: ${rem(12)};
`

function HomepageSocieteLuxPlusCards() {
  const intl = useIntl()
  const { variation } = useExperiment(ChertoExperiments.croHomepageSocieteLuxPlusCards)
  const onSocieteClick = useCallback(() => {
    trackClientEvent({
      subject: 'homepage_societe_card',
      action: 'clicked',
      category: 'homepage',
      type: 'interaction',
    })
  }, [])
  const onLuxPlusClick = useCallback(() => {
    trackClientEvent({
      subject: 'homepage_luxplus_card',
      action: 'clicked',
      category: 'homepage',
      type: 'interaction',
    })
  }, [])

  if (variation !== 'variant') return null

  return <section>
    <Group direction="vertical" tabletDirection="horizontal" gap={20} tabletVerticalAlign="stretch" data-testid="homepage-societe-luxplus-cards">
      <Card to={`/${LOYALTY_LANDING_PAGE}`} onClick={onSocieteClick}>
        <Group direction="vertical" desktopDirection="horizontal" desktopVerticalAlign="stretch" fullHeight>
          <SocieteBrand direction="horizontal" horizontalAlign="center" verticalAlign="center">
            <SocieteLogoContainer><LoyaltyProgramNameLogo aria-hidden="true" /></SocieteLogoContainer>
          </SocieteBrand>
          <Content direction="horizontal" verticalAlign="center" grow>
            <Group direction="vertical" gap={4} grow>
              <BodyText variant="medium" weight="bold">
                {intl.formatMessage(messages.societeHeadline)}
              </BodyText>
              <BodyText variant="medium" colour="light-foreground-alt">
                {intl.formatMessage(messages.societeDescription)}
              </BodyText>
            </Group>
            <ArrowIcon />
          </Content>
        </Group>
      </Card>
      <Card to={`/${LUXURY_PLUS_PATHS.DEFAULT_LANDING_PAGE}`} onClick={onLuxPlusClick}>
        <Group direction="vertical" desktopDirection="horizontal" desktopVerticalAlign="stretch" fullHeight>
          <LuxPlusBrand direction="horizontal" horizontalAlign="center" verticalAlign="center">
            <LuxPlusLogoContainer><LuxPlusLogo colour="inherit" aria-hidden="true" /></LuxPlusLogoContainer>
          </LuxPlusBrand>
          <Content direction="horizontal" verticalAlign="center" grow>
            <Group direction="vertical" gap={4} grow>
              <BodyText variant="medium" weight="bold">
                {intl.formatMessage(messages.luxPlusHeadline)}
              </BodyText>
              <BodyText variant="medium" colour="light-foreground-alt">
                {intl.formatMessage(messages.luxPlusDescription)}
              </BodyText>
            </Group>
            <ArrowIcon />
          </Content>
        </Group>
      </Card>
    </Group>
  </section>
}

export default HomepageSocieteLuxPlusCards
